<template>
  <!-- <nav>
    <router-link to="/">Home</router-link> |
    <router-link to="/about">About</router-link>
  </nav> -->
  <router-view v-slot="{ Component }">
    <keep-alive :include="cachedComponents">
      <component :is="Component" />
    </keep-alive>
  </router-view>
</template>

<script>
import { useGameStore } from '@/store/game.js'
import { useCommonStore } from '@/store/common.js'

export default {
  name: 'App',
  data() {
    return {
      liveChatSetupDone: false,
      checkInterval: null,
      lastUid: null
    }
  },
  computed: {
    cachedComponents() {
      const commonStore = useCommonStore()
      return commonStore.cachedComponents
    }
  },
  mounted() {
    this.initLiveChat()
    // 定期检查用户登录状态变化
    this.checkInterval = setInterval(() => {
      this.checkAndUpdateLiveChat()
    }, 2000) // 每2秒检查一次
    
    // 预加载所有小图标，避免显示时刷新
    this.preloadIcons()
  },
  beforeUnmount() {
    // 清理定时器
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
    }
  },
  methods: {
    initLiveChat() {
      // 检查 LiveChatWidget 是否已加载
      if (window.LiveChatWidget) {
        // 监听 ready 事件
        window.LiveChatWidget.on('ready', () => {
          this.setupLiveChat()
        })
        
        // 尝试立即设置（如果已经加载完成）
        setTimeout(() => {
          try {
            const state = window.LiveChatWidget.get('state')
            if (state) {
              this.setupLiveChat()
            }
          } catch (e) {
            // Widget 尚未准备好，等待 ready 事件
            console.log('等待 LiveChat Widget 准备就绪...')
          }
        }, 1000)
      } else {
        // 如果 LiveChatWidget 还未定义，等待一段时间后重试
        setTimeout(() => {
          this.initLiveChat()
        }, 500)
      }
    },
    
    checkAndUpdateLiveChat() {
      if (!window.LiveChatWidget) {
        return
      }
      
      const gameStore = useGameStore()
      const currentUid = gameStore.getUid()
      
      // 如果用户ID发生变化，更新 LiveChat 配置
      if (currentUid !== this.lastUid) {
        this.lastUid = currentUid
        this.setupLiveChat()
      }
    },
    
    setupLiveChat() {
      if (!window.LiveChatWidget) {
        return
      }
      
      const gameStore = useGameStore()
      
      // 隐藏问候语（每次设置时都尝试隐藏，确保生效）
      try {
        window.LiveChatWidget.call('hide_greeting')
        console.log('LiveChat 问候语已隐藏')
      } catch (e) {
        // 如果 Widget 尚未准备好，忽略错误
        if (e.message && !e.message.includes("before load")) {
          console.warn('隐藏 LiveChat 问候语失败:', e)
        }
      }
      
      // 检查用户是否已登录
      const uid = gameStore.getUid()
      const token = gameStore.getToken()
      
      if (uid && token) {
        // 用户已登录，设置用户ID
        try {
          // 使用 set_customer_name 传递用户ID
          window.LiveChatWidget.call('set_customer_name', `用户ID: ${uid}`)
          console.log('LiveChat 用户ID已设置:', uid)
        } catch (e) {
          // 如果 Widget 尚未准备好，忽略错误
          if (e.message && !e.message.includes("before load")) {
            console.warn('设置 LiveChat 用户ID失败:', e)
          }
        }
      }
    },
    
    // 预加载所有小图标
    preloadIcons() {
      // 定义所有需要预加载的小图标
      const iconPaths = [
        require('@/assets/111.png'),  // 我要充值
        require('@/assets/222.png'),  // 我要提现
        require('@/assets/333.png'),  // 积分记录
        require('@/assets/444.png'),  // 公告中心
        require('@/assets/laba.png'), // 喇叭图标
        require('@/assets/card_0.png'), // 轮播图
        require('@/assets/card_1.png'), // 轮播图
        // 房间图标
        require('@/assets/room_0.png'),
        require('@/assets/room_1.png'),
        require('@/assets/room_2.png'),
        require('@/assets/room_3.png'),
        require('@/assets/room_4.png'),
      ]
      
      // 预加载所有图标到浏览器缓存
      iconPaths.forEach(iconPath => {
        const img = new Image()
        img.src = iconPath
        // 不等待加载完成，让浏览器在后台缓存
      })
    }
  }
}
</script>

<style lang="scss">
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  // 支持 WebView 刘海屏适配
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

nav {
  padding: 30px;

  a {
    font-weight: bold;
    color: #2c3e50;

    &.router-link-exact-active {
      color: #42b983;
    }
  }
}

// 调整 Salesmartly 客服组件位置
// 这些选择器针对 Salesmartly 插件生成的元素
:deep(#smly-launcher-frame),
:deep([id*="salesmartly"]),
:deep([class*="salesmartly"]),
:deep(iframe[src*="salesmartly"]) {
  // 调整位置：右下角，但要避开底部导航栏
  bottom: 140px !important; // 留出底部导航栏的空间（60px高度 + 10px间距）
  right: 10px !important;
  z-index: 999 !important; // 确保在大多数元素之上，但不会覆盖关键UI
}

// 如果客服按钮是通过其他方式注入的，也可以用这个通用选择器
:deep(div[style*="position: fixed"][style*="bottom"]):has(iframe) {
  bottom: 140px !important;
  right: 10px !important;
}

// LiveChat 聊天窗口样式调整
// 控制打开后的聊天窗口高度为70%，避免与手机状态栏重叠
:deep(#chat-widget-container),
:deep([id*="livechat"]),
:deep(iframe[src*="livechatinc.com"]) {
  max-height: 70vh !important; // 限制最大高度为视口高度的70%
  height: 70vh !important;
  top: auto !important; // 取消顶部定位
  bottom: 0 !important; // 从底部开始
}

// 针对 LiveChat 的聊天窗口容器
:deep(div[data-testid="chat-widget-container"]),
:deep(div[class*="LiveChat"]),
:deep(div[id*="LiveChat"]) {
  max-height: 70vh !important;
  height: 70vh !important;
  top: auto !important;
  bottom: 0 !important;
}
</style>
